import Field from 'core/ui/forms/field';
import {known_chart_ids} from "hooks/useChartsStore";
import SessionContext from 'SessionContext';
import isEqual from "lodash.isequal";
import { useHistory, useLocation } from 'react-router-dom';
import LZString from 'lz-string';
import debounce from 'lodash.debounce';
import { useContext, useEffect, useRef, useState } from 'react';

const usStateFips = [
  { ID: "01", abr: "AL" }, { ID: "02", abr: "AK" }, { ID: "04", abr: "AZ" },
  { ID: "05", abr: "AR" }, { ID: "06", abr: "CA" }, { ID: "08", abr: "CO" },
  { ID: "09", abr: "CT" }, { ID: "10", abr: "DE" }, { ID: "11", abr: "DC" },
  { ID: "12", abr: "FL" }, { ID: "13", abr: "GA" }, { ID: "15", abr: "HI" },
  { ID: "16", abr: "ID" }, { ID: "17", abr: "IL" }, { ID: "18", abr: "IN" },
  { ID: "19", abr: "IA" }, { ID: "20", abr: "KS" }, { ID: "21", abr: "KY" },
  { ID: "22", abr: "LA" }, { ID: "23", abr: "ME" }, { ID: "24", abr: "MD" },
  { ID: "25", abr: "MA" }, { ID: "26", abr: "MI" }, { ID: "27", abr: "MN" },
  { ID: "28", abr: "MS" }, { ID: "29", abr: "MO" }, { ID: "30", abr: "MT" },
  { ID: "31", abr: "NE" }, { ID: "32", abr: "NV" }, { ID: "33", abr: "NH" },
  { ID: "34", abr: "NJ" }, { ID: "35", abr: "NM" }, { ID: "36", abr: "NY" },
  { ID: "37", abr: "NC" }, { ID: "38", abr: "ND" }, { ID: "39", abr: "OH" },
  { ID: "40", abr: "OK" }, { ID: "41", abr: "OR" }, { ID: "42", abr: "PA" },
  { ID: "44", abr: "RI" }, { ID: "45", abr: "SC" }, { ID: "46", abr: "SD" },
  { ID: "47", abr: "TN" }, { ID: "48", abr: "TX" }, { ID: "49", abr: "UT" },
  { ID: "50", abr: "VT" }, { ID: "51", abr: "VA" }, { ID: "53", abr: "WA" },
  { ID: "54", abr: "WV" }, { ID: "55", abr: "WI" }, { ID: "56", abr: "WY" },
  { ID: "60", abr: "AS" }, { ID: "66", abr: "GU" }, { ID: "69", abr: "MP" },
  { ID: "72", abr: "PR" }, { ID: "74", abr: "UM" }, { ID: "78", abr: "VI" },
];

const boundary_layers = [
  { layer_id: 'zipcodes', chart_id: known_chart_ids.zipcodes, series: 'ZIP', layer_prop: 'ZIP', friendly_name_prop: 'ZIP' },
  { layer_id: 'counties', chart_id: known_chart_ids.counties, series: 'COUNTY_FIPS', layer_prop: 'GEOID', friendly_name_prop: 'NAMELSAD' },
  { layer_id: 'congressional_districts', chart_id: known_chart_ids.congressional_districts, series: 'congressional_districts', layer_prop: 'GEOID', friendly_name_prop: 'NAMELSAD' },
  { layer_id: 'cbsa', chart_id: known_chart_ids.cbsa, series: 'CBSA', layer_prop: 'GEOID', friendly_name_prop: 'NAME' },
  { layer_id: 'states', chart_id: known_chart_ids.states, series: 'STATE', layer_prop: 'STUSPS', friendly_name_prop: 'NAME', layer_filters: ['!', ['==', ['get', 'REGION'], '9']] },
];

function BoundariesLayers(props) {
  const context = useContext(SessionContext);
  const history = useHistory();
  const location = useLocation();

  const [visible, setVisible] = useState('none');
  const [mapLayersReady, setMapLayersReady] = useState(false);

  const unlistenRef = useRef(null);
  const nextLocationRef = useRef(null);
  const locationPathnameRef = useRef(location.pathname);
  const prevFiltersRef = useRef(null);
  const prevCountRef = useRef(null);
  const visibleRef = useRef('none');
  const onFiltersUpdateRef = useRef(null);
  const contextRef = useRef(context);
  const mapEventHandlerRef = useRef(() => onFiltersUpdateRef.current());
  const debouncedUpdateMapView = useRef(debounce((_map) => {
    setTimeout(() => {
      _map.easeTo({
        zoom: _map.getZoom() + 0.000001,
        duration: 0
      });
    }, 600);
  }, 100)).current;

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);

  function getStateAbbr(id) {
    const abbr = usStateFips.find(item => item.ID === id);
    return (abbr !== undefined) ? abbr.abr : "";
  }

  function setLayerVisibility(map, layer_id, visibility) {
    map.setLayoutProperty(layer_id, 'visibility', visibility);
    map.setLayoutProperty(`${layer_id}_bounds`, 'visibility', visibility);
    map.setLayoutProperty(`${layer_id}_labels`, 'visibility', visibility);
  }

  function addBoundaryLayers(map, layer_id, text_field, data_field, layer_filters, base_chart_id) {
    const isNew = !map.getLayer(layer_id);
    if (isNew) {
      map.addLayer({
        'id': layer_id,
        "source": layer_id,
        "source-layer": layer_id,
        "layout": {"visibility": "none"},
        "type": "fill",
        "paint": {
          "fill-color": "rgba(0, 89, 188, 1)",
          "fill-opacity": 0
        }
      });
    }

    if (!map.getLayer(`${layer_id}_bounds`)) {
      map.addLayer({
        'id': `${layer_id}_bounds`,
        "source": layer_id,
        "source-layer": layer_id,
        "type": "line",
        "layout": {"visibility": "none"},
        "paint": {
          "line-color": "rgba(0, 89, 188, 1)",
          "line-width": 0.3
        },
        "minzoom": 0
      });
    }

    if (!map.getLayer(`${layer_id}_labels`)) {
      map.addLayer({
        'id': `${layer_id}_labels`,
        "source": layer_id,
        "source-layer": layer_id,
        "type": "symbol",
        "layout": {
          "text-field": `{${text_field}}`,
          "text-allow-overlap": false,
          "text-size": 12,
          "visibility": "none"
        },
        "paint": {
          "text-color": "#0059bc",
          "text-opacity": 1
        },
        "minzoom": 9
      });
    }

    if (layer_filters) {
      map.setFilter(layer_id, layer_filters);
      map.setFilter(`${layer_id}_bounds`, layer_filters);
      map.setFilter(`${layer_id}_labels`, layer_filters);
    }

    map.setPaintProperty(layer_id, 'fill-opacity',
      ["case",
        ["boolean", ["feature-state", "hover"], false], .4,
        ["boolean", ["feature-state", "filtered"], false], .2,
        0]);
    map.setPaintProperty(`${layer_id}_labels`, 'text-color',
      ["case",
        ["boolean", ["feature-state", "hover"], false], '#FFFFFF',
        '#0059bc']);

    if (isNew) {
      let hoveredFeature = null;
      map.on("mousemove", layer_id, (e) => {
        hoveredFeature && map.setFeatureState(hoveredFeature, {hover: false});
        if (e.features.length === 0) return;
        hoveredFeature = e.features[0];
        map.setFeatureState(hoveredFeature, {hover: true});
      });

      map.on("mouseleave", layer_id, () => {
        hoveredFeature && map.setFeatureState(hoveredFeature, {hover: false});
        hoveredFeature = null;
      });

      map.on('click', layer_id, (e) => {
        if (e.features.length === 0) return;

        const { chartsStore, crossfilterStore } = contextRef.current;

        const feature = e.features[0];
        const value = '' + feature.properties[data_field];
        const geoPrefix = crossfilterStore.geoPrefix || '';
        let chart_id = known_chart_ids[layer_id];
        let config = chartsStore.charts[chart_id];
        if (!config) {
          chart_id = geoPrefix + base_chart_id;
          config = chartsStore.charts[chart_id];
        }
        if (!config) return;
        let statefips = feature.properties["STATEFP"];
        const stateLabel = (
          base_chart_id === 'congress' || base_chart_id === 'county_fips'
        ) ? " - " + getStateAbbr(statefips) : "";

        const series = config.id;

        if (!feature.state.filtered) {
          crossfilterStore.add_filter(chart_id, config.ds + '.' + series, value, false, {friendly_name: feature.properties[text_field] + stateLabel, usState: getStateAbbr(statefips)});
        } else {
          crossfilterStore.remove_filter(chart_id, config.ds + '.' + series, value, false);
        }
      });
    }
  }

  function onFiltersUpdate() {
    const { map } = props;
    if (!map) return;

    if (!map?.isStyleLoaded?.() || !map.getStyle()) {
      return;
    }

    // check if our custom layers are in the map
    if (typeof map.getLayer !== "function" || !map.getLayer("states")) return;

    if (props.show) {
      const geoPrefix = context.crossfilterStore.geoPrefix || '';
      for (let {layer_id, layer_prop, chart_id: base_chart_id} of boundary_layers) {
        const chart_id = geoPrefix + base_chart_id;
        const selectedZips = (context.crossfilterStore.filters[chart_id] || []).map((filter) => filter.value);
        const visibleZips = map.queryRenderedFeatures({layers: [layer_id]});
        const exitingZipSelection = visibleZips.filter((f) => f.state.filtered && selectedZips.indexOf(f.properties[layer_prop]) === -1);
        const enteringZipSelection = visibleZips.filter((f) => !f.state.filtered && selectedZips.indexOf(f.properties[layer_prop]) > -1);
        exitingZipSelection.forEach((feature) => map.setFeatureState(feature, {filtered: false}));
        enteringZipSelection.forEach((feature) => map.setFeatureState(feature, {filtered: true}));
      }
    } else {
      hideBoundaryLayers();
    }
  }

  onFiltersUpdateRef.current = onFiltersUpdate;
  contextRef.current = context;

  function hideBoundaryLayers() {
    const map = props.map;
    if (!map._removed && visibleRef.current !== 'none') {
      boundary_layers.forEach(({ layer_id }) => {
        setLayerVisibility(map, layer_id, 'none');
      });
      setVisible('none');
      localStorage.setItem('explorer:layers:boundaries', JSON.stringify({ visible: 'none' }));
    }
  }

  function toggleLayer(layer_id) {
    if (!layer_id || layer_id === 'none') return;
    const map = props.map;
    if (visibleRef.current === layer_id) { return; }

    if (visibleRef.current && visibleRef.current !== 'none') {
      setLayerVisibility(map, visibleRef.current, 'none');
    }

    setVisible(layer_id);
    localStorage.setItem('explorer:layers:boundaries', JSON.stringify({ visible: layer_id }));
    setLayerVisibility(map, layer_id, 'visible');
  }

  function initMapLayers() {
    const map = props.map;

    for (let {layer_id, friendly_name_prop, layer_prop, layer_filters, chart_id: base_chart_id} of boundary_layers) {
      addBoundaryLayers(map, layer_id, friendly_name_prop, layer_prop, layer_filters, base_chart_id);
    }

    map.on('move', mapEventHandlerRef.current);
    map.on('zoom', mapEventHandlerRef.current);
    map.on('styleimagemissing', () => {});
    setMapLayersReady(true);
  }

  // Mount effect
  useEffect(() => {
    if (!props.map) return;
    const mapEventHandler = mapEventHandlerRef.current;

    unlistenRef.current = history.listen((loc) => {
      nextLocationRef.current = loc.pathname;
    });

    initMapLayers();

    prevFiltersRef.current = context.crossfilterStore.filters;
    prevCountRef.current = context.crossfilterStore.count;

    let visible_layer = JSON.parse(localStorage.getItem('explorer:layers:boundaries')) || {visible: "none"};
    let last_audience = JSON.parse(LZString.decompress(localStorage.getItem("am:audience:audience")) || "{}");

    // if it has a created_date is a saved, order, or model audience: ignore the localstorage data
    if ("created_date" in last_audience) {
      visible_layer.visible = "none";
    }

    toggleLayer(visible_layer.visible);
    onFiltersUpdate();
    setTimeout(() => {
      props.map.easeTo({
        zoom: props.map.getZoom() + 0.000001,
        duration: 0
      });
    }, 500);

    return () => {
      debouncedUpdateMapView.cancel();

      // update local storage only when user leaves the explorer page
      if (nextLocationRef.current && nextLocationRef.current !== locationPathnameRef.current) {
        localStorage.setItem('explorer:layers:boundaries', JSON.stringify({ visible: visibleRef.current }));
      }

      if (unlistenRef.current) unlistenRef.current();

      const { map } = props;
      if (!map) return;
      map.off('move', mapEventHandler);
      map.off('zoom', mapEventHandler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter/count change effect
  useEffect(() => {
    const x_filter = context.crossfilterStore;
    if (!isEqual(x_filter.count, prevCountRef.current) || !isEqual(x_filter.filters, prevFiltersRef.current)) {
      prevFiltersRef.current = x_filter.filters;
      prevCountRef.current = x_filter.count;
      debouncedUpdateMapView(props.map);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.crossfilterStore]);

  // mapLayersReady change effect
  useEffect(() => {
    if (mapLayersReady) {
      debouncedUpdateMapView(props.map);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLayersReady]);

  // visible change effect
  useEffect(() => {
    if (mapLayersReady) {
      debouncedUpdateMapView(props.map);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <>
      <h4>Boundaries</h4>
      <ul>
        <li>
          <Field label="None">
            <input type='radio'
                   onClick={() => hideBoundaryLayers()}
                   checked={visible === 'none'}
                   onChange={() => {}} />
          </Field>
        </li>
        <li>
          <Field label="States">
            <input type='radio' onClick={() => toggleLayer('states')} checked={visible === 'states'}
                   onChange={() => {}} />
          </Field>
        </li>
        <li>
          <Field label="Counties">
            <input type='radio' onClick={() => toggleLayer('counties')} checked={visible === 'counties'}
                   onChange={() => {}} />
          </Field>
        </li>
        <li>
          <Field label={<span>Zip Codes<sup>&trade;</sup></span>}>
            <input type='radio' onClick={() => toggleLayer('zipcodes')} checked={visible === 'zipcodes'}
                   onChange={() => {}} />
          </Field>
        </li>
        <li>
          <Field label="Congressional Districts">
            <input type='radio' onClick={() => toggleLayer('congressional_districts')} checked={visible === 'congressional_districts'}
                   onChange={() => {}} />
          </Field>
        </li>
        <li>
          <Field label="CBSA">
            <input type='radio' onClick={() => toggleLayer('cbsa')} checked={visible === 'cbsa'}
                   onChange={() => {}} />
          </Field>
        </li>
      </ul>
    </>
  );
}

export default BoundariesLayers;
